import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../redux/userSlice";
import { Container, Card, Form, Button, Row, Col, Modal, Spinner } from "react-bootstrap";
import { FaPlus, FaTimes } from "react-icons/fa";
import { FiEdit2, FiSave } from "react-icons/fi";
import UserLayout from "../components/UserLayout";
import UserAvatarFrame from "../components/UserAvatarFrame";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../css/app.css";
import "../css/Profile.css";
import { MedalIcon } from "../components/tickets/Tickets";
import { API_URL_All } from "../api";
import axiosInstance from "../axiosInstance";
import { setFilters } from "../redux/filterReducer";
import * as general from "../libs/general";

const API_URL = `${API_URL_All}/api/users`;

const emptyPasswordForm = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

const Profile = () => {
  const user = useSelector((state) => state.user.user);
  const [profileConfig, setProfileConfig] = useState(() => general.getLocalStorage("user_config") || {});
  const [customer, setCustomer] = useState(null);
  const dispatch = useDispatch();
  const [price, setPrice] = useState("");
  const [items, setItems] = useState([]);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const [hasPassword, setHasPassword] = useState(true);
  const [passwordSetupRequired, setPasswordSetupRequired] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState(emptyPasswordForm);
  const [passwordError, setPasswordError] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    const storedFilters = sessionStorage.getItem("userFilters");
    if (storedFilters) {
      const parsedFilters = JSON.parse(storedFilters);
      dispatch(setFilters(parsedFilters));
      sessionStorage.setItem("userFilters", JSON.stringify(parsedFilters));
    }
  }, [dispatch]);

  useEffect(() => {
    if (user?.email) {
      fetchCustomerDetails(user.email);
      loadProfileConfig();
    }
  }, [user?.email]);

  const loadProfileConfig = async () => {
    try {
      const response = await axiosInstance.get("/api/users/getConfig");
      if (response.data?.data) {
        setProfileConfig(response.data.data);
        general.setLocalStorage("user_config", response.data.data);
      }
    } catch (error) {
      console.error("Lỗi khi lấy cấu hình user:", error);
    }
  };

  useEffect(() => {
    return () => {
      if (avatarPreview) {
        URL.revokeObjectURL(avatarPreview);
      }
    };
  }, [avatarPreview]);

  const loadPasswordFlags = async () => {
    try {
      const res = await axiosInstance.get("/api/users/getProfile");
      const data = res?.data?.data;
      if (data) {
        setHasPassword(data.hasPassword === true);
        setPasswordSetupRequired(data.passwordSetupRequired === true);
      }
    } catch (error) {
      console.error("Lỗi khi lấy trạng thái mật khẩu:", error);
    }
  };

  const fetchCustomerDetails = async (email) => {
    try {
      const response = await axiosInstance.get(`${API_URL}/email/${email}`);
      setCustomer(response.data);
      setItems(response.data.addresses || []);
      setPrice(response.data.userLinePrice || "");
      await loadPasswordFlags();
    } catch (error) {
      console.error("Lỗi khi lấy dữ liệu khách hàng:", error);
    }
  };

  const needsCurrentPassword = hasPassword && !passwordSetupRequired;
  const isFirstPasswordSetup = passwordSetupRequired || !hasPassword;

  const openChangePasswordModal = () => {
    setPasswordForm(emptyPasswordForm);
    setPasswordError("");
    setShowChangePasswordModal(true);
  };

  const closeChangePasswordModal = () => {
    setShowChangePasswordModal(false);
    setPasswordForm(emptyPasswordForm);
    setPasswordError("");
  };

  const handlePasswordFieldChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
    if (passwordError) setPasswordError("");
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    if (!newPassword || !confirmPassword) {
      setPasswordError("Vui lòng nhập đầy đủ mật khẩu mới.");
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError("Mật khẩu mới phải có ít nhất 6 ký tự.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Mật khẩu mới và nhập lại mật khẩu không trùng khớp.");
      return;
    }
    if (needsCurrentPassword && !currentPassword.trim()) {
      setPasswordError("Vui lòng nhập mật khẩu hiện tại.");
      return;
    }
    setChangingPassword(true);
    setPasswordError("");
    try {
      const body = {
        currentPassword: needsCurrentPassword ? currentPassword : undefined,
        newPassword,
      };
      let res;
      try {
        res = await axiosInstance.put("/api/users/my/changePassword", body, {
          headers: { "Content-Type": "application/json" },
        });
      } catch (putErr) {
        if (putErr?.response?.status === 405) {
          res = await axiosInstance.post("/api/users/my/changePassword", body, {
            headers: { "Content-Type": "application/json" },
          });
        } else {
          throw putErr;
        }
      }
      if (res.data?.success) {
        toast.success(res.data?.message || "Đã đổi mật khẩu thành công.");
        closeChangePasswordModal();
        await loadPasswordFlags();
      } else {
        const msg = res.data?.message || "Không đổi được mật khẩu. Vui lòng thử lại.";
        setPasswordError(msg);
        toast.error(msg);
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Không đổi được mật khẩu. Vui lòng thử lại.";
      setPasswordError(msg);
      toast.error(msg);
    } finally {
      setChangingPassword(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomer((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInputChangeItem = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;
    setItems(updatedItems);
  };

  const handlePickAvatar = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !file.type.startsWith("image/")) {
      if (file) toast.warning("Vui lòng chọn file ảnh.");
      return;
    }
    setAvatarFile(file);
    setAvatarPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!customer?.id) return;

    setSaving(true);
    const isChild = profileConfig?.isChildAccount === true;
    const userDto = isChild
      ? {
          id: customer.id,
          email: customer.email,
          name: customer.name,
          phoneNumber: customer.phoneNumber,
          ...(customer.picture ? { picture: customer.picture } : {}),
        }
      : {
          ...customer,
          addresses: items,
          ...(price ? { userLinePrice: price } : {}),
        };

    try {
      const fd = new FormData();
      fd.append("user", new Blob([JSON.stringify(userDto)], { type: "application/json" }));
      if (avatarFile) {
        fd.append("avatar", avatarFile);
      }

      let response;
      try {
        response = await axiosInstance.put("/api/users/updateMyProfile", fd);
      } catch (putErr) {
        if (putErr?.response?.status === 405) {
          response = await axiosInstance.post("/api/users/updateMyProfile", fd);
        } else {
          throw putErr;
        }
      }

      if (response.data?.success === false) {
        toast.error(response.data?.message || "Có lỗi xảy ra khi cập nhật thông tin!");
        return;
      }

      toast.success(response.data?.message || "Cập nhật thành công!", { autoClose: 2000 });

      setAvatarFile(null);
      setAvatarPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });

      const refreshed = await axiosInstance.get(`${API_URL}/email/${user.email}`);
      const updatedCustomer = refreshed.data;
      setCustomer(updatedCustomer);
      setItems(updatedCustomer.addresses || []);

      const cfgRes = await axiosInstance.get("/api/users/getConfig");
      if (cfgRes.data?.data) {
        setProfileConfig(cfgRes.data.data);
        general.setLocalStorage("user_config", cfgRes.data.data);
      }
      const cfg = cfgRes.data?.data;
      const nextPicture = updatedCustomer.picture || cfg?.userPath || user?.picture;
      if (user) {
        dispatch(
          setUser({
            ...user,
            name: updatedCustomer.name,
            picture: nextPicture,
          })
        );
      }
    } catch (error) {
      console.error("Lỗi khi cập nhật thông tin khách hàng:", error);
      toast.error(error.response?.data?.message || "Có lỗi xảy ra khi cập nhật thông tin!");
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = (e) => {
    e.preventDefault();
    setItems([...items, {}]);
  };

  const handleDeleteItem = (index) => {
    const updatedItems = [...items];
    updatedItems.splice(index, 1);
    setItems(updatedItems);
  };

  const handleDefaultChange = (index) => {
    setItems(
      items.map((item, i) => ({
        ...item,
        defaultAddress: i === index,
      }))
    );
  };

  if (!customer) {
    return (
      <UserLayout>
        <Container className="mt-4 py-5 user-profile-page text-center text-muted">
          Đang tải…
        </Container>
      </UserLayout>
    );
  }

  const profileCustomerCode =
    (profileConfig?.code || customer?.customerCode || "").trim() || "—";
  const isChildAccount = profileConfig?.isChildAccount === true;
  const isMainCustomerAccount = profileConfig?.canManageChildUsers === true;

  return (
    <UserLayout>
      <Container className="mt-3 mb-4 user-profile-page main-content">
        <Form onSubmit={handleSubmit} className="user-profile-form">
          <Card className="user-profile-card shadow-sm">
            <div className="user-profile-header d-flex flex-column flex-md-row align-items-start gap-3 p-4 border-bottom">
              <div className="user-profile-header__main d-flex flex-column flex-sm-row align-items-start gap-3">
                <div className="user-profile__avatar-wrap position-relative">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="d-none"
                    onChange={handlePickAvatar}
                  />
                  <UserAvatarFrame
                    size="lg"
                    user={user}
                    config={profileConfig}
                    entityPicture={customer?.picture}
                    previewUrl={avatarPreview}
                  />
                  <button
                    type="button"
                    className="user-profile__avatar-edit btn btn-light rounded-circle shadow-sm"
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="Chọn ảnh đại diện"
                    title="Đổi ảnh đại diện"
                  >
                    <FiEdit2 size={16} aria-hidden />
                  </button>
                </div>
                <div className="user-profile-header__info pt-md-2">
                  <div className="user-profile-header__line fw-semibold fs-6">
                    {customer?.name || "—"}
                  </div>
                  <div className="user-profile-header__line text-muted">
                    {customer?.email || "—"}
                  </div>
                  <div className="user-profile-header__line text-muted">
                    {customer?.username || "—"}
                  </div>
                </div>
              </div>
            </div>

            <Card.Body className="p-4">
              <h6 className="user-profile-section-title">Thông tin liên hệ</h6>
              <Row className="g-3 mb-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Tên khách hàng</Form.Label>
                    <Form.Control
                      name="name"
                      value={customer?.name || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Mã khách hàng</Form.Label>
                    <Form.Control readOnly value={profileCustomerCode} />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Điện thoại</Form.Label>
                    <Form.Control
                      name="phoneNumber"
                      value={customer?.phoneNumber || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                {isChildAccount && (
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Tài khoản chính</Form.Label>
                      <Form.Control
                        readOnly
                        value={(profileConfig?.parentAccountName || "").trim() || "—"}
                      />
                    </Form.Group>
                  </Col>
                )}
                {isMainCustomerAccount && (
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Số user phụ thuộc</Form.Label>
                      <Form.Control readOnly value={profileConfig?.childUserCount ?? 0} />
                    </Form.Group>
                  </Col>
                )}
                {!isChildAccount && (
                  <>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>MST / CCCD</Form.Label>
                    <Form.Control
                      name="taxCode"
                      value={customer?.taxCode || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Email nhận hóa đơn, chứng từ</Form.Label>
                    <Form.Control
                      name="invoiceEmail"
                      value={customer?.invoiceEmail || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Địa chỉ văn phòng</Form.Label>
                    <Form.Control
                      name="officeAddress"
                      value={customer?.officeAddress || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                  </>
                )}
              </Row>

              {!isChildAccount && (
              <>
              <h6 className="user-profile-section-title">Tài khoản ngân hàng</h6>
              <Row className="g-3 mb-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>STK công ty</Form.Label>
                    <Form.Control
                      name="companyBankAccount"
                      value={customer?.companyBankAccount || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>STK cá nhân</Form.Label>
                    <Form.Control
                      name="personalBankAccount"
                      value={customer?.personalBankAccount || ""}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <h6 className="user-profile-section-title mb-0">Danh sách kho hàng</h6>
                <Button
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  className="d-inline-flex align-items-center gap-1"
                  onClick={handleAdd}
                >
                  <FaPlus size={12} />
                  Thêm kho
                </Button>
              </div>
              <div className="user-profile-warehouse-list mb-4">
                {items.length === 0 ? (
                  <p className="text-muted small mb-0 py-3 text-center">
                    Chưa có kho hàng nào
                  </p>
                ) : (
                  items.map((addr, index) => (
                    <div key={index} className="user-profile-warehouse-item">
                      <div className="user-profile-warehouse-item__default">
                        <Form.Check
                          type="radio"
                          name="defaultAddress"
                          label="Mặc định"
                          checked={!!addr.defaultAddress}
                          onChange={() => handleDefaultChange(index)}
                        />
                      </div>
                      <div className="user-profile-warehouse-item__fields flex-grow-1">
                        <Row className="g-2 mb-2">
                          <Col sm={6}>
                            <Form.Control
                              size="sm"
                              placeholder="Tên KH"
                              value={addr.name || ""}
                              onChange={(e) =>
                                handleInputChangeItem(index, "name", e.target.value)
                              }
                            />
                          </Col>
                          <Col sm={6}>
                            <Form.Control
                              size="sm"
                              placeholder="Số điện thoại"
                              value={addr.phone || ""}
                              onChange={(e) =>
                                handleInputChangeItem(index, "phone", e.target.value)
                              }
                            />
                          </Col>
                        </Row>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          size="sm"
                          placeholder="Địa chỉ"
                          value={addr.address || ""}
                          onChange={(e) =>
                            handleInputChangeItem(index, "address", e.target.value)
                          }
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm rounded-circle user-profile-warehouse-item__remove"
                        onClick={() => handleDeleteItem(index)}
                        title="Xoá"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))
                )}
              </div>

              <h6 className="user-profile-section-title">Thành tích</h6>
              <div className="user-profile-achievement">
                <MedalIcon rank={customer?.userRank} />
              </div>
              </>
              )}
            </Card.Body>

            <Card.Footer className="user-profile-footer d-flex justify-content-between align-items-center bg-light">
              <button
                type="button"
                className="btn btn-link btn-sm p-0 user-profile__change-password-link"
                onClick={openChangePasswordModal}
              >
                Thay đổi mật khẩu
              </button>
              <Button type="submit" variant="primary" size="sm" disabled={saving}>
                {saving ? "Đang lưu…" : "Cập nhật"}
              </Button>
            </Card.Footer>
          </Card>
        </Form>
      </Container>

      <Modal
        show={showChangePasswordModal}
        onHide={closeChangePasswordModal}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Thay đổi mật khẩu</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleChangePasswordSubmit}>
          <Modal.Body>
            {isFirstPasswordSetup && (
              <p className="text-muted small mb-3">
                Tài khoản chưa có mật khẩu đăng nhập (ví dụ đăng nhập bằng Google). Vui lòng
                thiết lập mật khẩu mới; không cần mật khẩu hiện tại.
              </p>
            )}
            {passwordError && <div className="alert alert-danger py-2 small">{passwordError}</div>}
            <Form.Group className="mb-3">
              <Form.Label>Tên đăng nhập</Form.Label>
              <Form.Control
                type="text"
                value={customer?.username || customer?.email || "—"}
                readOnly
                disabled
              />
            </Form.Group>
            {needsCurrentPassword && (
              <Form.Group className="mb-3">
                <Form.Label>Mật khẩu hiện tại</Form.Label>
                <Form.Control
                  type="password"
                  name="currentPassword"
                  value={passwordForm.currentPassword}
                  onChange={handlePasswordFieldChange}
                  autoComplete="current-password"
                />
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>Mật khẩu mới</Form.Label>
              <Form.Control
                type="password"
                name="newPassword"
                value={passwordForm.newPassword}
                onChange={handlePasswordFieldChange}
                autoComplete="new-password"
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Nhập lại mật khẩu mới</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordFieldChange}
                autoComplete="new-password"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="justify-content-end">
            <Button
              size="sm"
              variant="primary"
              type="submit"
              disabled={changingPassword}
              className="d-inline-flex align-items-center gap-2"
            >
              {changingPassword ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Đang lưu…
                </>
              ) : (
                <>
                  <FiSave size={16} aria-hidden />
                  Lưu mật khẩu
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </UserLayout>
  );
};

export default Profile;
